import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import type { WallMonitor } from '../api/types'
import { t } from '../i18n/lingua'

/**
 * Lo **schermo di riferimento** di una dashboard: il monitor per cui è
 * disegnata («1920x1080», in pixel CSS, cioè dopo lo zoom del monitor).
 *
 * Nell'editor la griglia si disegna **a quella misura** e si rimpicciolisce
 * per stare nella pagina (`CorniceSchermo`: `transform: scale`), con la
 * testata del sinottico e la riga «fine dello schermo» al posto giusto: ciò
 * che si vede è ciò che il monitor mostrerà, caratteri e tessere compresi.
 * Un 4K senza zoom fa i caratteri piccoli: lo si vede qui, prima di
 * accendere il monitor. Il riferimento viaggia con la dashboard
 * (`ConsoleDashboard.screen`), e «Sinottici pubblicati» avvisa quando il
 * monitor che la mostra ha un'altra misura.
 */
export interface Schermo {
  w: number
  h: number
}

/** The usual screens, as the panels name them. */
export const SCHERMI: { key: string; w: number; h: number; nome: string }[] = [
  { key: '1024x768', w: 1024, h: 768, nome: 'XGA 4:3' },
  { key: '1280x720', w: 1280, h: 720, nome: 'HD' },
  { key: '1280x1024', w: 1280, h: 1024, nome: 'SXGA 5:4' },
  { key: '1366x768', w: 1366, h: 768, nome: 'HD' },
  { key: '1600x900', w: 1600, h: 900, nome: 'HD+' },
  { key: '1920x1080', w: 1920, h: 1080, nome: 'Full HD' },
  { key: '1920x1200', w: 1920, h: 1200, nome: 'WUXGA' },
  { key: '2560x1440', w: 2560, h: 1440, nome: 'QHD' },
  { key: '3840x2160', w: 3840, h: 2160, nome: '4K UHD' },
]

/** «1920x1080», «1920×1080», «1920X1080» → {w, h}; anything else null. */
export function leggiSchermo(testo: string | null | undefined): Schermo | null {
  const m = /^(\d{3,5})[x×X](\d{3,5})$/.exec((testo ?? '').trim())
  if (!m) return null
  const w = Number(m[1])
  const h = Number(m[2])
  if (w < 320 || h < 320 || w > 16384 || h > 16384) return null
  return { w, h }
}

export const chiaveSchermo = (s: Schermo): string => `${s.w}x${s.h}`
export const etichettaSchermo = (s: Schermo): string => `${s.w}×${s.h}`

/**
 * The wall page around its grid, in px: the padding of `.sinottico`, its
 * two-row header (brand and title, the numbers and the clock) and the gap
 * below it. The header's height depends on the fonts: this is an estimate,
 * good to a few pixels.
 */
export const CORNICE_SINOTTICO = { sopra: 146, sotto: 18, lati: 22 }

/** The gap of the grid, as in `.griglia`. */
const GAP = 14
const RIGHE = 12

/** The height of a row of the free matrix on that screen: what the wall computes from its area. */
export function altezzaRigaPer(schermo: Schermo): number {
  return Math.max(28, Math.floor((schermo.h - CORNICE_SINOTTICO.sopra - CORNICE_SINOTTICO.sotto - GAP * (RIGHE - 1)) / RIGHE))
}

/**
 * The screen a monitor shows, in CSS pixels: its panel mode (the resolution
 * set from the console, else the one it reports) over its zoom, turned when
 * it is rotated by 90° or 270°. Null when it has not said.
 */
export function schermoDelMonitor(m: WallMonitor): Schermo | null {
  const base = leggiSchermo(m.settings.resolution) ?? leggiSchermo(m.state.system?.display_mode)
  if (!base) return null
  const zoom = m.settings.zoom && m.settings.zoom > 0 ? m.settings.zoom : 1
  const w = Math.round(base.w / zoom)
  const h = Math.round(base.h / zoom)
  const ruotato = m.settings.rotation === 90 || m.settings.rotation === 270
  return ruotato ? { w: h, h: w } : { w, h }
}

/**
 * The frame: the children are laid out at the screen's width and scaled to
 * the room there is, with the wall's header drawn as a band and the bottom
 * edge of the screen marked; what sticks out below is what the wall would
 * not show. The children get the scale, for the pointer maths of the grid.
 */
export function CorniceSchermo({ schermo, children }: {
  schermo: Schermo
  children: (scala: number) => ReactNode
}) {
  const esterno = useRef<HTMLDivElement>(null)
  const area = useRef<HTMLDivElement>(null)
  const [largEditor, setLargEditor] = useState(0)
  const [altArea, setAltArea] = useState(0)
  useEffect(() => {
    const e = esterno.current
    const a = area.current
    if (!e || !a || typeof ResizeObserver === 'undefined') return
    // contentRect is the layout box: a transform on the box does not change it.
    const ro = new ResizeObserver((entries) => {
      for (const en of entries) {
        if (en.target === e) setLargEditor(Math.round(en.contentRect.width))
        else if (en.target === a) setAltArea(Math.round(en.contentRect.height))
      }
    })
    ro.observe(e)
    ro.observe(a)
    return () => ro.disconnect()
  }, [])
  const scala = largEditor > 0 ? Math.min(1, largEditor / schermo.w) : 1
  const utile = schermo.h - CORNICE_SINOTTICO.sopra - CORNICE_SINOTTICO.sotto
  const altezza = Math.max(schermo.h, altArea + CORNICE_SINOTTICO.sopra + CORNICE_SINOTTICO.sotto)
  return (
    <div ref={esterno} className="cornice" style={{ height: Math.ceil(altezza * scala) }}>
      <div className="cornice__schermo" style={{ width: schermo.w, height: altezza, transform: `scale(${scala})` }}>
        <div className="cornice__testata" style={{ height: CORNICE_SINOTTICO.sopra, padding: `0 ${CORNICE_SINOTTICO.lati}px` }} aria-hidden="true">
          <span>{t('Testata del sinottico: marchio, titolo, numeri e orologio')}</span>
          <span className="mono">{etichettaSchermo(schermo)} · {t('scala {p} %', { p: Math.round(scala * 100) })}</span>
        </div>
        <div ref={area} className="cornice__area" style={{ padding: `0 ${CORNICE_SINOTTICO.lati}px`, minHeight: utile }}>
          {children(scala)}
        </div>
        <div className="cornice__fine" style={{ top: schermo.h }} aria-hidden="true"><span>{t('fine dello schermo')}</span></div>
      </div>
    </div>
  )
}

function schermiUguali(a: Schermo | null, b: Schermo | null): boolean {
  return !!a && !!b && a.w === b.w && a.h === b.h
}

/**
 * The picker: a usual screen, one of the paired monitors (its effective
 * screen: resolution over zoom, turned if rotated), or any width × height;
 * «verticale» turns the chosen screen; «zoom del monitor» divides it, as
 * the monitor's zoom does. What it hands back is the effective «WxH».
 */
export function SceltaSchermo({ valore, onCambia, monitor }: {
  valore: string | null | undefined
  onCambia: (schermo: string | null) => void
  monitor?: WallMonitor[]
}) {
  const attuale = leggiSchermo(valore)
  // What the person picked, to rebuild the effective screen when the
  // orientation or the zoom change; from the stored value when it opens.
  const predefinita = () => {
    if (!attuale) return { base: '', verticale: false, zoom: 1, w: 1920, h: 1080 }
    const diritto = SCHERMI.find((s) => s.w === attuale.w && s.h === attuale.h)
    if (diritto) return { base: diritto.key, verticale: false, zoom: 1, w: diritto.w, h: diritto.h }
    const girato = SCHERMI.find((s) => s.w === attuale.h && s.h === attuale.w)
    if (girato) return { base: girato.key, verticale: true, zoom: 1, w: girato.w, h: girato.h }
    return { base: 'altro', verticale: false, zoom: 1, w: attuale.w, h: attuale.h }
  }
  const [scelta, setScelta] = useState(predefinita)
  // The stored value changed from outside (an import, another dashboard
  // opened): the picker follows it.
  useEffect(() => {
    const mio = effettivo(scelta)
    if ((mio ? chiaveSchermo(mio) : null) !== (attuale ? chiaveSchermo(attuale) : null)) setScelta(predefinita())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valore])

  const effettivo = (s: typeof scelta): Schermo | null => {
    let w = s.w
    let h = s.h
    if (s.base.startsWith('monitor:')) {
      const m = monitor?.find((x) => `monitor:${x.id}` === s.base)
      const sm = m ? schermoDelMonitor(m) : null
      if (!sm) return null
      // The monitor's own zoom and rotation are already in its screen.
      return sm
    }
    if (s.base === '') return null
    const preset = SCHERMI.find((p) => p.key === s.base)
    if (preset) { w = preset.w; h = preset.h }
    const zoom = s.zoom > 0 ? s.zoom : 1
    const e = { w: Math.round(w / zoom), h: Math.round(h / zoom) }
    return s.verticale ? { w: e.h, h: e.w } : e
  }
  const applica = (s: typeof scelta) => {
    setScelta(s)
    const e = effettivo(s)
    if (!schermiUguali(e, attuale)) onCambia(e ? chiaveSchermo(e) : null)
  }
  const conMonitor = (monitor ?? []).map((m) => ({ m, s: schermoDelMonitor(m) })).filter((x) => x.s)
  const daMonitor = scelta.base.startsWith('monitor:')

  return (
    <span className="scelta-schermo eagle-piccolo" title={t('La griglia si disegna alla misura di quel monitor, in scala: caratteri e tessere come li vedrai a parete.')}>
      <label className="muted">
        {t('Schermo di riferimento')}{' '}
        <select value={scelta.base} onChange={(e) => applica({ ...scelta, base: e.target.value })} aria-label={t('Schermo di riferimento')}>
          <option value="">{t('nessuno (questa finestra)')}</option>
          {SCHERMI.map((s) => <option key={s.key} value={s.key}>{etichettaSchermo(s)} · {s.nome}</option>)}
          {conMonitor.length > 0 && (
            <optgroup label={t('Come un monitor accoppiato')}>
              {conMonitor.map(({ m, s }) => <option key={m.id} value={`monitor:${m.id}`}>{m.name} · {etichettaSchermo(s!)}</option>)}
            </optgroup>
          )}
          <option value="altro">{t('altro…')}</option>
        </select>
      </label>
      {scelta.base === 'altro' && (
        <>
          <input type="number" min={320} max={16384} value={scelta.w} aria-label={t('Larghezza in pixel')}
                 onChange={(e) => applica({ ...scelta, w: Number(e.target.value) || 0 })} style={{ width: 72 }} />
          ×
          <input type="number" min={320} max={16384} value={scelta.h} aria-label={t('Altezza in pixel')}
                 onChange={(e) => applica({ ...scelta, h: Number(e.target.value) || 0 })} style={{ width: 72 }} />
        </>
      )}
      {scelta.base !== '' && !daMonitor && (
        <>
          <label className="checkbox">
            <input type="checkbox" checked={scelta.verticale} onChange={(e) => applica({ ...scelta, verticale: e.target.checked })} />
            {' '}{t('verticale')}
          </label>
          <label className="muted">
            {t('zoom del monitor')}{' '}
            <input type="number" min={0.5} max={3} step={0.05} value={scelta.zoom} aria-label={t('zoom del monitor')}
                   onChange={(e) => applica({ ...scelta, zoom: Number(e.target.value) || 1 })} style={{ width: 60 }} />
          </label>
        </>
      )}
      {attuale && <span className="muted mono">{etichettaSchermo(attuale)}</span>}
    </span>
  )
}
